import React, { useState } from 'react';
import { Card, Timeline, Typography, Space, Empty, Modal, Table, Tag, Segmented } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  PushpinOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  StopOutlined,
  FileTextOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

interface TimelineEvent {
  started: string;
  ended: string | null;
  comment: string | null;
  event_type: string;
  location_name: string | null;
}

interface TestJigDetail {
  metadata: string;
  value: string;
  unit: string | null;
}

interface TestJigReport {
  report_id: string;
  test_result: string;
  entry_time: string;
  test_jig_id: string;
  comment: string | null;
  details: TestJigDetail[];
}

interface NodeLifeTimelineProps {
  events: TimelineEvent[];
  testJigReports: TestJigReport[];
  currentTicketId: number;
}

const eventColorMap: Record<string, string> = {
  tested: 'green',
  mapped: 'blue',
  battery_changed: 'orange',
  rework: 'purple',
  decommissioned: 'gray',
};

const eventIconMap: Record<string, React.ReactNode> = {
  tested: <CheckCircleOutlined />,
  mapped: <PushpinOutlined />,
  battery_changed: <ThunderboltOutlined />,
  rework: <ToolOutlined />,
  decommissioned: <StopOutlined />,
};

function getEventKey(event: TimelineEvent): string {
  const lower = event.event_type.toLowerCase();
  if (lower === 'tested') return 'tested';
  if (lower === 'mapped') return 'mapped';
  if (lower.includes('battery')) return 'battery_changed';
  if (lower.includes('rework')) return 'rework';
  return 'decommissioned';
}

// A "tested" lifecycle event and the test-jig reports come from two unrelated
// tables (m_node_history vs sf_testjig) with no shared key — the only link is
// the day. A node is often tested several times in one day (retests, jig
// sweeps), so a single day can hold many reports. Return ALL of them, oldest
// first, and let the caller present a picker — matching only the first report
// (the old find()) made every same-day test show identical content.
function matchReports(event: TimelineEvent, reports: TestJigReport[]): TestJigReport[] {
  const eventDay = dayjs(event.started).format('YYYY-MM-DD');
  return reports
    .filter((r) => dayjs(r.entry_time).format('YYYY-MM-DD') === eventDay)
    .sort((a, b) => new Date(a.entry_time).getTime() - new Date(b.entry_time).getTime());
}

const detailColumns = [
  {
    title: 'Test',
    dataIndex: 'metadata',
    key: 'metadata',
    render: (v: string) => <Typography.Text code style={{ fontSize: 12 }}>{v}</Typography.Text>,
  },
  {
    title: 'Value',
    dataIndex: 'value',
    key: 'value',
    render: (v: string) => {
      const lower = (v || '').toLowerCase();
      if (lower === 'true') return <Tag color="success">Pass</Tag>;
      if (lower === 'false') return <Tag color="error">Fail</Tag>;
      return <Typography.Text style={{ fontSize: 12 }}>{v}</Typography.Text>;
    },
  },
  {
    title: 'Unit',
    dataIndex: 'unit',
    key: 'unit',
    render: (v: string | null) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{v || '--'}</Typography.Text>,
  },
];

const NodeLifeTimeline: React.FC<NodeLifeTimelineProps> = ({ events, testJigReports }) => {
  // The modal shows one report at a time but holds the whole day's set so the
  // user can flip between multiple same-day tests via the picker.
  const [dayReports, setDayReports] = useState<TestJigReport[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selectedReport = dayReports[selectedIdx] ?? null;

  if (events.length === 0) {
    return (
      <Card
        title={<Space><ClockCircleOutlined /><span>Node Life History</span></Space>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lifecycle events found" />
      </Card>
    );
  }

  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.started).getTime() - new Date(a.started).getTime()
  );

  const handleEventClick = (event: TimelineEvent) => {
    const key = getEventKey(event);
    if (key !== 'tested') return;
    const reports = matchReports(event, testJigReports);
    if (reports.length > 0) {
      setDayReports(reports);
      setSelectedIdx(0);
    }
  };

  const closeModal = () => setDayReports([]);

  return (
    <Card
      title={<Space><ClockCircleOutlined /><span>Node Life History</span></Space>}
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {events.length} events
        </Typography.Text>
      }
    >
      <Timeline
        items={sortedEvents.map((event) => {
          const key = getEventKey(event);
          const isTested = key === 'tested';
          const hasReport = isTested && matchReports(event, testJigReports).length > 0;

          return {
            color: eventColorMap[key] || 'blue',
            dot: eventIconMap[key] || <FileTextOutlined />,
            children: (
              <div
                onClick={() => handleEventClick(event)}
                style={{ cursor: hasReport ? 'pointer' : 'default' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space size={4}>
                    <Typography.Text strong style={{ fontSize: 13 }}>
                      {event.event_type.replace(/_/g, ' ')}
                    </Typography.Text>
                    {hasReport && (
                      <ExperimentOutlined style={{ fontSize: 12, color: '#006EB7' }} />
                    )}
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(event.started).format('YYYY-MM-DD')}
                  </Typography.Text>
                </div>
                {event.comment && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {event.comment}
                  </Typography.Text>
                )}
                {event.location_name && (
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      @ {event.location_name}
                    </Typography.Text>
                  </div>
                )}
              </div>
            ),
          };
        })}
      />

      <Modal
        title={
          <Space>
            <ExperimentOutlined />
            <span>Test Jig Report</span>
            {selectedReport && (
              selectedReport.test_result === 'True'
                ? <Tag color="success">PASS</Tag>
                : <Tag color="error">FAIL</Tag>
            )}
          </Space>
        }
        open={dayReports.length > 0}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        {dayReports.length > 1 && (
          <Segmented
            block
            size="small"
            value={selectedIdx}
            onChange={(v) => setSelectedIdx(Number(v))}
            style={{ marginBottom: 12 }}
            options={dayReports.map((r, i) => ({
              value: i,
              label: (
                <Space size={4}>
                  <span>{dayjs(r.entry_time).format('HH:mm')}</span>
                  {r.test_result === 'True'
                    ? <Tag color="success" style={{ margin: 0 }}>P</Tag>
                    : <Tag color="error" style={{ margin: 0 }}>F</Tag>}
                </Space>
              ),
            }))}
          />
        )}
        {selectedReport && (
          <>
            <Space direction="vertical" size={4} style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Date: {dayjs(selectedReport.entry_time).format('YYYY-MM-DD HH:mm')}
                {dayReports.length > 1 && ` · report ${selectedIdx + 1} of ${dayReports.length} this day`}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Test Jig ID: {selectedReport.test_jig_id}
              </Typography.Text>
              {selectedReport.comment && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Comment: {selectedReport.comment}
                </Typography.Text>
              )}
            </Space>
            <Table
              dataSource={selectedReport.details}
              columns={detailColumns}
              rowKey="metadata"
              size="small"
              pagination={false}
              scroll={{ y: 400 }}
            />
          </>
        )}
      </Modal>
    </Card>
  );
};

export default NodeLifeTimeline;
